import {
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Dispatch, SetStateAction } from "react";
import toast from "react-hot-toast";
import { useConfirm } from "@/components/providers/confirmProvider";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import { useDeleteCardOnFileMutation } from "@/graphql/mutations/deleteCardOnFile.graphql.types";
import { useGenerateStripeClientSecretMutation } from "@/graphql/mutations/generateStripeClientSecret.graphql.types";
import { useUpdateDefaultCardForClientMutation } from "@/graphql/mutations/updateDefaultCardForClient.graphql.types";
import useWallet from "@/hooks/clients/useWallet";
import { hasDjangoMutationError } from "@/utils/djangoMutationError";
import useErrorLogger from "@/utils/useErrorLogger";

type AddCardParams = {
  zipCode: string;
  setShowAddNewCardDrawer: Dispatch<SetStateAction<boolean>>;
  setNewCardsAsDefault?: boolean;
};

type CreditCardActions = {
  handleAddCard: (params: AddCardParams) => Promise<void>;
  handleDeleteCard: (cardId: string) => Promise<void>;
  handleSetDefaultCard: (cardId: string) => Promise<void>;
  handleAttemptToDeleteDefaultCard: (
    setShowAddNewCardDrawer: Dispatch<SetStateAction<boolean>>
  ) => Promise<void>;
};

const MODAL_CONFIG = {
  SET_DEFAULT_CARD: {
    confirmButtonText: "Confirm",
    discardButtonText: "Cancel",
    title: "Set this credit card as default?",
    description: "We will use this card to collect your membership dues.",
  } as const,

  DELETE_DEFAULT_CARD: {
    confirmButtonText: "Add Another Card",
    discardButtonText: "Cancel",
    title: "Remove default credit card?",
    description:
      "This MedSpa requires a credit card on file. You cannot delete your current card because no other card is linked to your account. To proceed, please add a new card or contact the MedSpa for assistance.",
  } as const,

  DELETE_CARD: {
    confirmButtonText: "Confirm",
    discardButtonText: "Cancel",
    title: "Remove this credit card?",
    description: "This operation can't be reversed",
  } as const,
} as const;

export default function useCreditCardActions(
  clientIdFromProps?: string
): CreditCardActions {
  const { getTokenContext } = useClientAccessToken();
  const stripe = useStripe();
  const elements = useElements();
  const logError = useErrorLogger();
  const { getConfirm } = useConfirm();
  const { clientId, refetchWallet, cardList } = useWallet(clientIdFromProps);

  const [generateClientSecret] = useGenerateStripeClientSecretMutation();
  const [updateDefaultCard] = useUpdateDefaultCardForClientMutation();
  const [deleteCardOnFile] = useDeleteCardOnFileMutation();

  const handleAddCard = async ({
    zipCode,
    setShowAddNewCardDrawer,
    setNewCardsAsDefault,
  }: AddCardParams) => {
    if (!stripe || !elements) return;

    const loadingToastId = toast.loading("Adding the card...");

    try {
      const contextWithToken = getTokenContext();
      const { data } = await generateClientSecret({
        variables: { clientId },
        ...contextWithToken,
      });

      const clientSecret = data.generateCardSetupIntent.clientSecret;
      const { error, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: {
              address: { postal_code: zipCode },
            },
          },
        }
      );

      if (error) {
        toast.error(error.message, { id: loadingToastId });
        return;
      }

      const newCardId = setupIntent.payment_method as string;
      const isFirstCard = cardList?.length === 0;

      if (isFirstCard || setNewCardsAsDefault) {
        await updateDefaultCard({
          variables: { cardId: newCardId, clientId },
          ...contextWithToken,
        });
        toast.success("The default card has been added successfully!", {
          id: loadingToastId,
        });
      } else {
        toast.success("The card has been added successfully!", {
          id: loadingToastId,
        });
      }
      refetchWallet();
    } catch (error) {
      toast.error(
        hasDjangoMutationError(error)
          ? error.message
          : "Unable to add the card. Please contact our support team.",
        { id: loadingToastId }
      );
      logError(error);
    } finally {
      setShowAddNewCardDrawer(false);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    const shouldDelete = await getConfirm(MODAL_CONFIG.DELETE_CARD);
    if (!shouldDelete) return;

    const loadingToastId = toast.loading("Removing the credit card...");

    try {
      await deleteCardOnFile({
        variables: { cardId, clientId },
        ...getTokenContext(),
      });
      await refetchWallet();
      toast.success("The credit card has been removed!", {
        id: loadingToastId,
      });
    } catch (e) {
      toast.error(
        hasDjangoMutationError(e)
          ? e.message
          : "Couldn't remove the credit card",
        { id: loadingToastId }
      );
      logError(
        `Error when removing the card ${cardId} from client ${clientId}`,
        e
      );
    }
  };

  const handleAttemptToDeleteDefaultCard = async (
    setShowAddNewCardDrawer: Dispatch<SetStateAction<boolean>>
  ) => {
    const shouldAddAnotherCard = await getConfirm(
      MODAL_CONFIG.DELETE_DEFAULT_CARD
    );

    if (!shouldAddAnotherCard) return;
    setShowAddNewCardDrawer(true);
  };

  const handleSetDefaultCard = async (cardId: string) => {
    const shouldSetDefault = await getConfirm(MODAL_CONFIG.SET_DEFAULT_CARD);

    if (!shouldSetDefault) return;

    try {
      await toast.promise(
        updateDefaultCard({
          variables: { cardId, clientId },
          ...getTokenContext(),
        }).then(() => refetchWallet()),
        {
          loading: "Setting the default credit card...",
          success: "The default credit card has been set!",
          error: "Couldn't set the default credit card.",
        }
      );
    } catch (e) {
      logError(
        `Error when setting the card ${cardId} from client ${clientId} as default`,
        e
      );
    }
  };

  return {
    handleAddCard,
    handleDeleteCard,
    handleSetDefaultCard,
    handleAttemptToDeleteDefaultCard,
  };
}
